// Client for the AI endpoints. API keys never travel to the browser — the
// server stores them encrypted and only ever returns a masked hint.

export type ProviderId = "anthropic" | "openai" | "google";

export interface ProviderInfo {
  id: ProviderId;
  label: string;
  keysUrl: string;
  models: string[];
}

export interface AiCredential {
  id: string;
  provider: ProviderId;
  model: string;
  keyHint: string;
  scope: "user" | "org";
  isActive: boolean;
}

// "quickadd"/"quickadd-short" strip the quick-add bar's shorthand (#project, p1,
// ~30m, today) from the visible title — the metadata is preserved separately.
export type RewriteMode =
  | "polish"
  | "shorten"
  | "actionable"
  | "quickadd"
  | "quickadd-short";

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api/ai${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new Error(
      (body.message as string) || (body.error as string) || `Request failed (${res.status})`,
    );
  }
  return body as T;
}

export const listProviders = () =>
  call<{ providers: ProviderInfo[] }>("/providers").then((r) => r.providers);

export const listCredentials = () =>
  call<{ credentials: AiCredential[] }>("/credentials").then((r) => r.credentials);

export const addCredential = (input: {
  provider: ProviderId;
  model: string;
  apiKey: string;
  scope: "user" | "org";
}) => call<{ ok: true; id: string }>("/credentials", { method: "POST", body: JSON.stringify(input) });

export const activateCredential = (id: string) =>
  call<{ ok: true }>(`/credentials/${id}/activate`, { method: "POST" });

export const deleteCredential = (id: string) =>
  call<{ ok: true }>(`/credentials/${id}`, { method: "DELETE" });

export const testCredential = (id: string) =>
  call<{ ok: true }>(`/credentials/${id}/test`, { method: "POST" });

export const rewrite = (text: string, mode: RewriteMode = "polish") =>
  call<{ suggestion: string; provider: string; model: string }>("/rewrite", {
    method: "POST",
    body: JSON.stringify({ text, mode }),
  });

export type UsageScope = "me" | "workspace";
export type UsageRange = "month" | "30d" | "all";

export interface UsageModelRow {
  provider: ProviderId;
  model: string;
  requests: number;
  inputTokens: number;
  outputTokens: number;
  cost: number | null; // null = model has no configured rate
}

export interface UsageSummary {
  scope: UsageScope;
  range: UsageRange;
  totals: {
    requests: number;
    inputTokens: number;
    outputTokens: number;
    cost: number;
    costComplete: boolean; // false if some usage had no rate (cost is a floor)
  };
  byModel: UsageModelRow[];
  daily: { date: string; cost: number }[];
}

export const getUsage = (scope: UsageScope, range: UsageRange) =>
  call<UsageSummary>(`/usage?scope=${scope}&range=${range}`);
